import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus } from "@fortawesome/free-solid-svg-icons";
import MyCollapse from "../../../../Common/MyCollapse";
import MyPagination from "../../../../Common/MyPagination";
import AssetList from "../../../../Assets/AssetList";
import { useGetAssetsQuery } from "../../../../store/slices/mapsSlice";

export interface AssetInputProps {
  values: any[];
  onChange: (values: any[]) => void;
  isMultiple?: boolean;
  hideList?: boolean;
}

export default function AssetInput({
  values,
  onChange,
  isMultiple,
  hideList,
}: AssetInputProps) {
  const [show, setShow] = useState(false);
  const [tobeRemoved, setTobeRemoved] = useState<string[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ page: 0, perPage: 10 });
  const [replace, setReplace] = useState(false);

  const { data: assetsData } = useGetAssetsQuery(
    {
      page: pagination.page,
      perPage: pagination.perPage,
      search,
      ignore: values,
    },
    { ignore: !show }
  );

  const { data: selectedAssetsData } = useGetAssetsQuery({
    page: pagination.page,
    perPage: pagination.perPage,
    search,
    assets: values,
  });

  const assetByUuid = useMemo(() => {
    const entries = (assetsData?.assets || []).map((asset) => [
      asset.Uuid,
      asset,
    ]);
    return Object.fromEntries(entries);
  }, [assetsData]);

  return (
    <>
      <div className="p-2 bg-light rounded-2">
        {values && values.length > 0 && !hideList && (
          <MyCollapse heading={`${values.length || "No"} assets selected.`}>
            <div className="mb-2">
              <div className="d-flex gap-2 flex-wrap align-items-center mb-2">
                <strong>Selected Assets</strong>
                {tobeRemoved.length > 0 && (
                  <button
                    className="btn btn-outline-danger border-0"
                    onClick={() => {
                      onChange(values.filter((v) => !tobeRemoved.includes(v)));
                      setTobeRemoved([]);
                    }}
                  >
                    Remove selected
                  </button>
                )}
              </div>
              <AssetList
                showTable
                assets={selectedAssetsData?.assets || []}
                actions={(asset) => {
                  const assetUuid = asset.Uuid || "";
                  const checked = tobeRemoved.includes(assetUuid);

                  return (
                    <div className="form-check m-0 d-flex justify-content-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTobeRemoved((prev) => [...prev, assetUuid]);
                          } else {
                            setTobeRemoved((prev) =>
                              prev.filter((uuid) => uuid !== assetUuid)
                            );
                          }
                        }}
                      />
                    </div>
                  );
                }}
              />
            </div>
          </MyCollapse>
        )}
        {(isMultiple || values.length === 0) && (
          <button className="btn btn-link p-0" onClick={() => setShow(true)}>
            Select Assets
          </button>
        )}
      </div>

      <Modal
        show={show}
        onHide={() => {
          setShow(false);
        }}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Assets</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ minHeight: 720 }}>
            <div className="bg-colored-reverse p-3 rounded-3 d-flex flex-wrap align-items-end mb-3 gap-2">
              <input
                className="form-control"
                style={{ maxWidth: 360 }}
                placeholder="Search assets"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination((prev) => ({ ...prev, page: 0 }));
                }}
              />
            </div>
            <AssetList
              showTable
              assets={assetsData?.assets || []}
              actions={(asset) => {
                const assetUuid = asset.Uuid || "";
                const checked = selectedAssets.includes(assetUuid);

                return (
                  <div className="form-check m-0 d-flex justify-content-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAssets((prev) =>
                            isMultiple ? [...prev, assetUuid] : [assetUuid]
                          );
                        } else {
                          setSelectedAssets((prev) =>
                            prev.filter((uuid) => uuid !== assetUuid)
                          );
                        }
                      }}
                    />
                  </div>
                );
              }}
            />
            <MyPagination
              total={assetsData?.total || 0}
              onPaginate={(page: number, perPage: number) => {
                setPagination({ page, perPage });
              }}
            />
          </div>
          <div className="d-flex flex-row justify-content-end align-items-center mb-3 mt-2">
            <div className="form-check me-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="replace-assets"
                checked={replace}
                onChange={(e) => setReplace(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="replace-assets">
                Replace existing assets
              </label>
            </div>
            {isMultiple ? (
              <button
                className="btn btn-colored"
                onClick={() => {
                  onChange(
                    replace ? selectedAssets : [...values, ...selectedAssets]
                  );
                  setShow(false);
                  setSelectedAssets([]);
                  setReplace(false);
                }}
                disabled={selectedAssets.length === 0}
              >
                Save
              </button>
            ) : (
              <button
                className="btn btn-colored"
                onClick={() => {
                  if (selectedAssets.length > 0) {
                    onChange([selectedAssets[selectedAssets.length - 1]]);
                  }
                  setShow(false);
                  setSelectedAssets([]);
                  setReplace(false);
                }}
                disabled={selectedAssets.length === 0}
              >
                Select
              </button>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
